/**
 * Expense.tsx
 * Log an expense.
 * Fields: Date, Description, Amount, PaidBy (partner names, default = logged-in partner),
 *   Notes, optional Receipt photo.
 * Category is always 'Variable' (owner decision: partners only log variable expenses).
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../auth'
import {
  getConfig,
  getExpenseAccounts,
  submitExpense,
  readExpenseReceipt,
  ApiError,
} from '../api'
import PhotoCapture from '../components/PhotoCapture'
import type { Partner, WaveExpenseAccount } from '../types'

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

type PageState = 'form' | 'submitting' | 'done'

export default function Expense() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [partners, setPartners] = useState<Partner[]>([])
  const [accounts, setAccounts] = useState<WaveExpenseAccount[]>([])
  const [accountId, setAccountId] = useState('')
  const [accountsError, setAccountsError] = useState('')
  const [date, setDate] = useState(today())
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [paidBy, setPaidBy] = useState('')
  const [notes, setNotes] = useState('')
  const [receiptKey, setReceiptKey] = useState<string | undefined>(undefined)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [pageState, setPageState] = useState<PageState>('form')

  // ---- OCR status (after a receipt photo is uploaded) ---------------------
  // Reads amount/date/vendor/description/suggestedCategory from the receipt.
  // For suggestedCategory, we case-insensitively match the model's value
  // against the Wave account list to set the Category dropdown.
  type OcrState = 'idle' | 'reading' | 'done' | 'error'
  const [ocrState, setOcrState] = useState<OcrState>('idle')
  const [ocrMsg, setOcrMsg] = useState<string>('')

  useEffect(() => {
    getConfig()
      .then((cfg) => {
        setPartners(cfg.partners)
        setPaidBy(cfg.caller?.id ?? '')
      })
      .catch(() => {
        // Non-fatal
      })

    // Fetch the curated Wave expense-account list for the Category dropdown.
    // If this fails we surface a clear inline error and disable submit — the
    // server requires accountId, so submitting without one would just 400.
    getExpenseAccounts()
      .then((resp) => {
        setAccounts(resp.accounts)
        if (resp.accounts.length > 0) {
          setAccountId(resp.accounts[0].id)
        }
      })
      .catch((err: unknown) => {
        setAccountsError(
          err instanceof Error
            ? `Could not load categories: ${err.message}`
            : 'Could not load categories.',
        )
      })
  }, [user])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!date) return setError('Date is required.')
    if (!description.trim()) return setError('Description is required.')
    if (!amount) return setError('Amount is required.')
    const amt = parseFloat(amount)
    if (isNaN(amt) || amt < 0)
      return setError('Amount must be a valid non-negative number.')
    if (!accountId) return setError('Category is required.')

    setPageState('submitting')
    try {
      const result = await submitExpense({
        date,
        // Wave is now the source of truth for categorization via accountId.
        // We keep `category: 'Variable'` for now because the server schema
        // still requires it and downstream sheet logic reads column B —
        // can be retired once the migration off the legacy column lands.
        category: 'Variable',
        description: description.trim(),
        amount: amt,
        paidBy,
        notes,
        accountId,
        ...(receiptKey ? { receiptKey } : {}),
      })
      if (!result.ok) {
        setError(result.message || 'Could not save.')
        setPageState('form')
        return
      }
      setSuccessMsg(result.message)
      setPageState('done')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        navigate('/not-authorized')
        return
      }
      setError(err instanceof Error ? err.message : 'Unexpected error.')
      setPageState('form')
    }
  }

  function handleLogAnother() {
    setDate(today())
    setDescription('')
    setAmount('')
    setNotes('')
    setReceiptKey(undefined)
    setError('')
    setSuccessMsg('')
    setOcrState('idle')
    setOcrMsg('')
    setPageState('form')
  }

  /**
   * After the partner uploads a receipt photo, call readExpenseReceipt to
   * pull amount/date/vendor/description/suggestedCategory and prefill any
   * fields the partner hasn't already filled in. Suggested category is
   * matched case-insensitively against the Wave account list — accountId
   * is set to the matching account.
   */
  async function handleReceiptUploaded(key: string): Promise<void> {
    setReceiptKey(key)
    setOcrState('reading')
    setOcrMsg('Reading receipt…')
    try {
      const r = await readExpenseReceipt(key)
      if (r.confidence === 'low') {
        setOcrState('error')
        setOcrMsg(
          r.reason
            ? `Could not read receipt (${r.reason}). Enter values manually.`
            : 'Could not read this receipt. Enter values manually.',
        )
        return
      }
      const filled: string[] = []
      if (r.amount != null && amount === '') {
        setAmount(String(r.amount))
        filled.push('Amount')
      }
      if (r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date) && date === today()) {
        setDate(r.date)
        filled.push('Date')
      }
      // Build a "vendor — description" or just description for the field.
      if (description === '') {
        const desc = [r.vendor, r.description].filter(Boolean).join(' — ')
        if (desc) {
          setDescription(desc)
          filled.push('Description')
        }
      }
      if (r.suggestedCategory && accounts.length > 0) {
        const wanted = r.suggestedCategory.trim().toLowerCase()
        const match = accounts.find((a) => a.name.toLowerCase() === wanted)
          ?? accounts.find((a) => a.name.toLowerCase().includes(wanted))
        if (match && match.id !== accountId) {
          setAccountId(match.id)
          filled.push(`Category (${match.name})`)
        }
      }
      const verb = r.confidence === 'high' ? 'Filled' : 'Suggested (please verify):'
      setOcrState('done')
      setOcrMsg(
        filled.length > 0
          ? `${verb} ${filled.join(', ')}.`
          : 'Receipt uploaded; no new fields to fill.',
      )
    } catch (err) {
      setOcrState('error')
      setOcrMsg(err instanceof Error ? err.message : 'Receipt read failed.')
    }
  }

  return (
    <div className="wrap">
      <div className="brand">
        <div className="brand-logo">N73AA</div>
        <div className="brand-tag">
          <Link to="/" className="back-link">&#8592; Back</Link>
          &ensp;Beechcraft Bonanza N35 &mdash; Log an Expense
        </div>
      </div>

      {user && (
        <div className="who">
          Logging as: <strong>{user.name}</strong>
        </div>
      )}

      {pageState !== 'done' ? (
        <form className="card" onSubmit={handleSubmit} noValidate>
          {error && <div className="msg msg-err">{error}</div>}

          <label htmlFor="date">Date</label>
          <input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            autoFocus
          />

          <label htmlFor="category">Category</label>
          {accountsError ? (
            <div className="msg msg-err">{accountsError}</div>
          ) : (
            <select
              id="category"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              required
              disabled={accounts.length === 0}
            >
              {accounts.length === 0 ? (
                <option value="">Loading…</option>
              ) : (
                accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))
              )}
            </select>
          )}

          <label htmlFor="description">Description</label>
          <input
            id="description"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Magneto repair"
            required
          />

          <label htmlFor="amount">Amount ($)</label>
          <input
            id="amount"
            type="number"
            step="0.01"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 250.00"
            required
          />

          <label htmlFor="paidBy">Paid By</label>
          <select
            id="paidBy"
            value={paidBy}
            onChange={(e) => setPaidBy(e.target.value)}
          >
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.id})
              </option>
            ))}
          </select>

          <label>Receipt Photo (optional)</label>
          <PhotoCapture
            label="expense"
            date={date}
            partnerId={paidBy || 'unknown'}
            buttonLabel="📷 Take receipt photo (auto-fills fields)"
            onUploadComplete={handleReceiptUploaded}
            onUploadError={(msg) => {
              setOcrState('error')
              setOcrMsg(msg)
            }}
          />
          {ocrMsg && (
            <p
              className="field-note"
              style={{ color: ocrState === 'error' ? '#c5221f' : '#137333' }}
            >
              {ocrMsg}
            </p>
          )}

          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Remarks…"
          />

          <button
            className="btn btn-expense"
            type="submit"
            disabled={pageState === 'submitting'}
          >
            {pageState === 'submitting' ? 'Saving…' : 'Log Expense'}
          </button>
          {pageState === 'submitting' && (
            <div className="spin">Saving to the log…</div>
          )}
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
        </form>
      ) : (
        <div className="card">
          <div className="msg msg-ok">{successMsg}</div>
          <div className="confirm-actions">
            <button className="btn btn-expense" onClick={handleLogAnother}>
              Log another expense
            </button>
            <button className="btn btn-neutral" onClick={() => navigate('/')}>
              Back to menu
            </button>
          </div>
        </div>
      )}

      <div className="page-foot">N73AA partnership log.</div>
    </div>
  )
}
